/**
 * useNavBadges — live counts for sidebar nav badges.
 *
 * Returns a map keyed by NavItem id → count, so AppLayout can decorate the nav:
 *   - requests:     pending document-requests THIS org must provide (someone else asked)
 *   - practice-crm: retainer clients whose invoice is due to be raised (billing)
 *
 * Cheap HEAD count queries, shared across the app and refreshed on org change
 * and on the live tick (which includes window focus).
 *
 * @since 3.3.0
 */
import { supabase } from '@/db/supabase';
import { createSharedRead } from '@/shared/lib/sharedRead';
import { useAuth } from './useAuth';
import { useLiveStore } from './useLiveRefresh';

async function countBadges(orgId: string, userId: string): Promise<Record<string, number>> {
    const today = new Date().toISOString().slice(0, 10);

    const [reqRes, billRes, partnerRes] = await Promise.all([
        // requests this org needs to act on (not the ones I raised)
        (() => {
            let q = supabase.from('document_requests')
                .select('id', { count: 'exact', head: true })
                .eq('org_id', orgId).eq('status', 'pending');
            if (userId) q = q.neq('requested_by', userId);
            return q;
        })(),
        // retainer clients due to be invoiced
        supabase.from('practice_clients')
            .select('id', { count: 'exact', head: true })
            .eq('practice_org_id', orgId)
            .eq('billing_model', 'retainer')
            .gt('retainer_amount', 0)
            .lte('next_invoice_date', today),
        // new referral signups the consultant hasn't seen yet
        supabase.rpc('get_partner_unread_count'),
    ]);

    return {
        requests: reqRes.count ?? 0,
        'practice-crm': billRes.count ?? 0,
        partner: Number(partnerRes.data ?? 0),
    };
}

// fastphone-2: one set of counts per user and business for the whole app,
// refreshed by the live tick (tab focus, a change to the books), not by a mount.
const badgesRead = createSharedRead<Record<string, number>>((key) => {
    const [orgId, userId] = key.split('|');
    return countBadges(orgId, userId);
});
if (typeof window !== 'undefined') {
    // A test that mocks the live module without the store simply goes without.
    try { useLiveStore.subscribe(() => badgesRead.refresh()); } catch { /* no live tick */ }
}

const NONE: Record<string, number> = {};

export function useNavBadges(): Record<string, number> {
    const orgId = useAuth((s) => s.currentOrg?.id);
    const userId = useAuth((s) => s.profile?.id) ?? '';
    return badgesRead.use(orgId ? `${orgId}|${userId}` : null).value ?? NONE;
}
